// NEW_FILE_JUSTIFIED: new page component for /insurance/types/new/extract route — pre-registered in routing but not yet implemented
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the standard Convex React client package — not a convex backend module
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig — four levels up from src/pages/insurance/types/
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TypeWizardStepper } from '@/components/insurance/types/Wizard/Stepper';
import { PolicyDropzone } from '@/components/insurance/types/Extract/PolicyDropzone';
import { ExtractInfoRail } from '@/components/insurance/types/Extract/ExtractInfoRail';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useAiPolicyExtraction } from '@/hooks/useAiPolicyExtraction';
import { isValidProviderId } from '@/lib/type-guards';
import type { TInsuranceProviderListItem, IPolicyDropzoneFile } from '@/@types/insuranceBuilder';
import type { Id } from '../../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import

const STEPS = [
  { key: 'source' as const, label: 'Source' },
  { key: 'extract' as const, label: 'Extract' },
  { key: 'builder' as const, label: 'Builder' },
  { key: 'review' as const, label: 'Review' },
];

type TExtractFormState = { providerId: Id<'insuranceProviders'> | undefined; name: string }; // LOCAL_TYPE_JUSTIFIED: small bundle used only in this file to avoid 3-useState violation

export function ExtractPolicyPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const providers = useQuery(
    api['insurance/providers'].list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );

  const [form, setForm] = useState<TExtractFormState>({ providerId: undefined, name: '' });
  const [selectedFile, setSelectedFile] = useState<IPolicyDropzoneFile | null>(null);
  const { stage, errorMessage, resultTypeId, startUpload, reset } = useAiPolicyExtraction();

  useEffect(() => {
    if (stage === 'success' && resultTypeId) {
      navigate(`/insurance/types/${resultTypeId}?aiExtracted=1`);
    }
  }, [stage, resultTypeId, navigate]);

  if (orgLoading || !currentOrg) return null;

  const isBusy = stage === 'uploading' || stage === 'queued' || stage === 'processing';
  const canSubmit = !!selectedFile && !isBusy && stage !== 'success';

  const ctaLabel =
    stage === 'uploading'
      ? 'Uploading…'
      : stage === 'queued'
        ? 'Queued…'
        : stage === 'processing'
          ? 'Extracting with AI…'
          : stage === 'error'
            ? 'Try again'
            : stage === 'success'
              ? 'Continue'
              : 'Awaiting upload';

  const handleSubmit = async () => {
    if (!selectedFile) return;
    if (stage === 'error') reset();
    await startUpload(selectedFile.file, {
      providerId: form.providerId,
      name: form.name.trim() || undefined,
    });
  };

  return (
    <div className="min-h-full bg-background-secondary">
      <header className="border-b bg-card px-6 py-3">
        <TypeWizardStepper steps={STEPS} currentIndex={1} />
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 px-10 py-8 max-w-6xl mx-auto">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/insurance/types/new')}
              className="px-2 gap-1"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              <span className="sr-only">Back to source selection</span>
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-foreground">AI extract from policy</h1>
              <p className="text-sm text-muted-foreground">
                Upload an existing policy PDF or photo — we'll detect every field for you to review.
              </p>
            </div>
          </div>

          <Card className="p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="extract-provider">Provider (optional)</Label>
                <Select
                  value={form.providerId ?? ''}
                  onValueChange={v =>
                    setForm(prev => ({
                      ...prev,
                      providerId: isValidProviderId(v) ? v : undefined,
                    }))
                  }
                  disabled={isBusy}
                >
                  <SelectTrigger id="extract-provider">
                    <SelectValue placeholder="AI will suggest if blank" />
                  </SelectTrigger>
                  <SelectContent>
                    {(providers ?? []).map((p: TInsuranceProviderListItem) => (
                      <SelectItem key={p._id} value={p._id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="extract-name">Type name (optional)</Label>
                <Input
                  id="extract-name"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="AI will suggest if blank"
                  disabled={isBusy}
                />
              </div>
            </div>

            <PolicyDropzone
              selectedFile={selectedFile}
              onFileSelected={setSelectedFile}
              onRemove={() => setSelectedFile(null)}
              disabled={isBusy}
            />

            {stage === 'error' && errorMessage && (
              <p role="alert" className="text-sm text-destructive">
                {errorMessage}
              </p>
            )}
          </Card>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => navigate('/insurance/types/new')}>
              Skip and start blank
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {ctaLabel}
            </Button>
          </div>
        </div>

        <ExtractInfoRail />
      </div>
    </div>
  );
}
